<script setup>
import {
  FlexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useVueTable,
} from "@tanstack/vue-table";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  useTemplateRef,
  watchEffect,
} from "vue";

const props = defineProps({
  data: { type: Array, required: true },
  columns: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  // What an empty table says. Name the rows: "No data" reads as a failure
  emptyMessage: { type: String, default: "No data" },
  error: { type: String, default: "" },
  enableSorting: { type: Boolean, default: false },
  // Free-text filter, matched against every column that holds a string
  globalFilter: { type: String, default: "" },
  // Per-column filters as tanstack takes them: [{ id, value }]. A column that
  // filters on something other than its displayed value needs its own `filterFn`.
  columnFilters: { type: Array, default: () => [] },
});

const emit = defineEmits(["update:globalFilter", "update:columnFilters"]);

const slots = useSlots();
const sorting = ref([]);

const body = useTemplateRef("body");
const tableEl = useTemplateRef("tableEl");
// The columns' floors can add up to more than the card has to give. CSS cannot tell
// when that happens, so measure, and let the table scroll inside the card then.
const overflows = ref(false);
let resizeObserver;

onMounted(() => {
  let bodyContentWidth = 0;
  resizeObserver = new ResizeObserver((entries) => {
    const bodyEntry = entries.find((entry) => entry.target === body.value);
    if (bodyEntry) bodyContentWidth = bodyEntry.contentRect.width;
    overflows.value = tableEl.value.getBoundingClientRect().width > bodyContentWidth + 1;
  });
  resizeObserver.observe(body.value);
  resizeObserver.observe(tableEl.value);
});

onBeforeUnmount(() => resizeObserver.disconnect());

const resolve = (updater, current) => (typeof updater === "function" ? updater(current) : updater);

// `meta.hidden` marks a column that exists only to filter on, e.g. an archived flag
const columnVisibility = computed(() =>
  Object.fromEntries(
    props.columns
      .filter((column) => column.meta?.hidden)
      .map((column) => [column.id ?? column.accessorKey, false]),
  ),
);

const table = useVueTable({
  get data() {
    return props.data;
  },
  get columns() {
    return props.columns;
  },
  get enableSorting() {
    return props.enableSorting;
  },
  state: {
    get sorting() {
      return sorting.value;
    },
    get globalFilter() {
      return props.globalFilter;
    },
    get columnFilters() {
      return props.columnFilters;
    },
    get columnVisibility() {
      return columnVisibility.value;
    },
  },
  onSortingChange: (updater) => {
    sorting.value = resolve(updater, sorting.value);
  },
  onGlobalFilterChange: (updater) => {
    emit("update:globalFilter", resolve(updater, props.globalFilter));
  },
  onColumnFiltersChange: (updater) => {
    emit("update:columnFilters", resolve(updater, props.columnFilters));
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
});

const rowCount = computed(() => table.getRowModel().rows.length);

const filteredAway = computed(() => props.data.length > 0 && rowCount.value === 0);

// Tanstack ignores a filter whose column it cannot find, so the filter silently does
// nothing. Say so instead.
if (import.meta.env.DEV) {
  watchEffect(() => {
    const ids = props.columns.map((column) => column.id ?? column.accessorKey);
    for (const { id } of props.columnFilters.filter(({ id }) => !ids.includes(id))) {
      // eslint-disable-next-line no-console
      console.warn(`DataTableV2: filtering on "${id}", but no column has that id`);
    }
  });
}

defineExpose({ table });
</script>

<template>
  <div class="card">
    <div ref="body" class="body" :class="{ scrolls: overflows }">
      <table ref="tableEl" class="table">
        <thead>
          <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :style="header.column.columnDef.meta?.style"
              :class="{
                sortable: header.column.getCanSort(),
                sorted: header.column.getIsSorted(),
                truncate: header.column.columnDef.meta?.truncate,
              }"
              :aria-hidden="header.column.id === 'spacer' || undefined"
              :tabindex="header.column.getCanSort() ? 0 : undefined"
              :aria-sort="
                header.column.getIsSorted() === 'asc'
                  ? 'ascending'
                  : header.column.getIsSorted() === 'desc'
                    ? 'descending'
                    : header.column.getCanSort()
                      ? 'none'
                      : undefined
              "
              @click="header.column.getToggleSortingHandler()?.($event)"
              @keydown.enter="header.column.getToggleSortingHandler()?.($event)"
              @keydown.space.prevent="header.column.getToggleSortingHandler()?.($event)"
            >
              <slot :name="`header-${header.column.id}`" :header :column="header.column">
                <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
              </slot>
              <span v-if="header.column.getIsSorted()" class="sort-indicator">
                {{ header.column.getIsSorted() === "asc" ? "↑" : "↓" }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading" class="status-row">
            <td :colspan="table.getVisibleLeafColumns().length" class="status-cell">
              <div class="status-content">
                <svg class="spinner" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle
                    cx="10"
                    cy="10"
                    r="8"
                    stroke="currentColor"
                    stroke-width="2"
                    opacity="0.2"
                  />
                  <path
                    d="M10 2a8 8 0 0 1 8 8"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
                <span>Loading…</span>
              </div>
            </td>
          </tr>
          <tr v-else-if="error" class="status-row">
            <td :colspan="table.getVisibleLeafColumns().length" class="status-cell status-error">
              <div class="status-content">
                <span>{{ error }}</span>
              </div>
            </td>
          </tr>
          <tr v-else-if="table.getRowModel().rows.length === 0" class="status-row">
            <td :colspan="table.getVisibleLeafColumns().length" class="status-cell">
              <div class="status-content">
                <span>{{ filteredAway ? "No matches" : emptyMessage }}</span>
              </div>
            </td>
          </tr>
          <template v-else>
            <tr v-for="row in table.getRowModel().rows" :key="row.id">
              <td
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :style="cell.column.columnDef.meta?.style"
                :class="{ truncate: cell.column.columnDef.meta?.truncate }"
                :aria-hidden="cell.column.id === 'spacer' || undefined"
              >
                <slot :name="`cell-${cell.column.id}`" :row="row.original" :cell>
                  <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                </slot>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="slots.footer || rowCount > 0" class="footer">
      <slot name="footer">
        <span>{{ rowCount }} {{ rowCount === 1 ? "result" : "results" }}</span>
      </slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/layout";
@use "@/styles/palette" as p;
@use "@/styles/mixins/typography";

.card {
  border: 1px solid c.$border-light;
  border-radius: 8px;
  background-color: c.$background-ui;
  box-shadow: 0 1px 3px rgb(0 0 0 / 6%);
}

.body {
  padding: 0 16px;
}

// A scrolling body is the sticky header's scroll root, which unpins it, and the
// `top` offset would then push the header down the table. So the header stays put.
.body.scrolls {
  overflow-x: auto;

  thead th {
    position: static;
  }
}

.table {
  width: 100%;
  border-collapse: collapse;

  @include typography.medium("regular");

  th,
  td {
    padding: 0.625rem 1.25rem;
    text-align: left;
  }

  thead th {
    position: sticky;
    top: var(--table-header-top, #{layout.$content-header-height});
    z-index: 1;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0;
    color: c.$text-subtle;
    background-color: c.$background-ui;
    box-shadow: inset 0 -1px 0 c.$border-light;
    white-space: nowrap;
    user-select: none;
    padding: 0.5rem 1.25rem;
  }

  tbody tr {
    border-bottom: 1px solid c.$border-light;
    transition: background-color 120ms ease;

    &:last-child {
      border-bottom: none;
    }

    &.status-row {
      cursor: default;
    }

    &:not(.status-row):hover {
      background-color: p.$black-alpha-5;
    }
  }

  tbody td {
    height: 44px;
    padding-block: 0;
    vertical-align: middle;
    color: c.$text;
  }
}

.truncate {
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sortable {
  cursor: pointer;

  &:hover {
    color: c.$main;
  }
}

.sorted {
  color: c.$main;
}

.sort-indicator {
  margin-left: 0.25rem;
  font-size: 0.625rem;
}

.status-cell {
  text-align: center;
  padding: 3rem 1rem;
  color: c.$text-subtle;
}

.status-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.status-error {
  color: c.$destructive;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spinner {
  animation: spin 0.8s linear infinite;
  color: c.$text-subtle;
}

.footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid c.$border-light;

  @include typography.small("regular");

  color: c.$text-subtle;
}
</style>
